// Imported from the config module rather than the `@features/balanceLedger` barrel: the barrel
// pulls in `customerBalanceConfig`, which imports this file.
import type {
  BalanceStatementResponse,
  BalanceSummaryResponse,
  FetchBalanceStatementExportParams,
  FetchBalanceStatementParams,
  FetchBalanceSummaryExportParams,
  FetchBalanceSummaryParams,
} from '@features/balanceLedger/config/balanceSideConfig'
import type { BalanceExportFormat, ExportFileResult } from '@features/balanceLedger/types/balanceExport'
import { buildBalanceStatementQueryParams } from '@features/balanceLedger/utils/buildBalanceStatementQueryParams'
import { buildBalanceSummaryQueryParams } from '@features/balanceLedger/utils/buildBalanceSummaryQueryParams'
import { fetchBalanceExportFile } from '@features/balanceLedger/utils/fetchBalanceExportFile'

import { getRequest } from '../../utils'

// AUDIT-131 freezes the debtor summary on the same contract as the creditor one, so this reuses the
// side-agnostic request/response aliases instead of redeclaring an identical shape.
export const fetchDebtorBalanceSummary = ({
  organizationId,
  period,
  page,
  pageSize,
  sortBy,
  sortDirection,
  search,
  contactIds,
  balanceDirection,
}: FetchBalanceSummaryParams): Promise<BalanceSummaryResponse> => {
  const queryParams = buildBalanceSummaryQueryParams({
    period,
    page,
    pageSize,
    sortBy,
    sortDirection,
    search,
    contactIds,
    balanceDirection,
  })

  return getRequest(`/v2/organizations/${organizationId}/debtorBalanceSummary?${queryParams}`)
}

// AUDIT-132 freezes the debtor statement on the same contract as the creditor one, so this reuses
// the side-agnostic request/response aliases instead of redeclaring an identical shape.
export const fetchDebtorBalanceStatement = ({
  organizationId,
  contactId,
  period,
  search,
  page,
  pageSize,
  sortBy,
  sortDirection,
}: FetchBalanceStatementParams): Promise<BalanceStatementResponse> => {
  const queryParams = buildBalanceStatementQueryParams({
    period,
    search,
    page,
    pageSize,
    sortBy,
    sortDirection,
  })

  return getRequest(`/v2/organizations/${organizationId}/debtorBalanceStatement/${contactId}?${queryParams}`)
}

const getDebtorBalanceSummaryExportFallbackFileName = (format: BalanceExportFormat): string =>
  `Customer-balance.${format}`

export const fetchDebtorBalanceSummaryExport = async ({
  organizationId,
  format,
  period,
  sortBy,
  sortDirection,
  search,
  contactIds,
  balanceDirection,
}: FetchBalanceSummaryExportParams): Promise<ExportFileResult> => {
  const queryParams = buildBalanceSummaryQueryParams({
    period,
    sortBy,
    sortDirection,
    search,
    contactIds,
    balanceDirection,
  })
  const querySuffix = queryParams ? `?${queryParams}` : ''
  const path = `/v2/organizations/${organizationId}/debtorBalanceSummary.${format}${querySuffix}`

  return fetchBalanceExportFile(path, format, getDebtorBalanceSummaryExportFallbackFileName(format))
}

const getDebtorBalanceStatementExportFallbackFileName = (format: BalanceExportFormat): string => `Statement.${format}`

export const fetchDebtorBalanceStatementExport = async ({
  organizationId,
  contactId,
  format,
  period,
  search,
}: FetchBalanceStatementExportParams): Promise<ExportFileResult> => {
  const queryParams = buildBalanceStatementQueryParams({
    period,
    search,
  })
  const querySuffix = queryParams ? `?${queryParams}` : ''
  const path = `/v2/organizations/${organizationId}/debtorBalanceStatement/${contactId}.${format}${querySuffix}`

  return fetchBalanceExportFile(path, format, getDebtorBalanceStatementExportFallbackFileName(format))
}
